/**
 * Footer Section Navigation
 * Handles smooth navigation to footer section pages using history API
 * Mobile only functionality
 */

class FooterSectionNavigation {
  constructor() {
    this.isMobile = window.innerWidth < 750;
    this.mainContent = document.querySelector('#MainContent');
    this.footerTriggers = document.querySelectorAll('.footer__marble-trigger');
    this.lastFocusedElement = null;
    
    if (this.isMobile) {
      this.init();
    }
    
    // Re-check on resize
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 750;
      
      if (!wasMobile && this.isMobile) {
        this.init();
      }
    });
  }
  
  init() {
    this.attachEventListeners();
    this.handlePopState();
    
    // Also check if we're on a footer section page on initial load
    if (window.location.pathname.includes('footer-section')) {
      const urlParams = new URLSearchParams(window.location.search);
      const sectionParam = urlParams.get('section');
      if (sectionParam) {
        // Wait for DOM to be fully ready
        setTimeout(() => {
          this.populateSectionContent(sectionParam);
        }, 200);
      }
    }
  }
  
  attachEventListeners() {
    // Links now work normally with full page redirect - no interception needed
    // Handle browser back/forward buttons (kept for compatibility)
    window.addEventListener('popstate', (e) => {
      // If navigating back/forward, let browser handle it normally
      // No need to intercept since we're doing full page loads now
    });
  }
  
  navigateToSection(heading) {
    const sectionParam = heading.toLowerCase();
    const url = `/pages/footer-section?section=${encodeURIComponent(sectionParam)}`;
    
    // Push state to history
    history.pushState(
      { footerSection: sectionParam },
      '',
      url
    );
    
    // Load content
    this.loadSectionContent(sectionParam, true);
  }
  
  async loadSectionContent(sectionParam, isNewNavigation) {
    if (!this.mainContent) return;
    
    // Add loading state
    this.mainContent.classList.add('is-loading');
    
    try {
      const url = `/pages/footer-section?section=${encodeURIComponent(sectionParam)}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to load content');
      }
      
      const html = await response.text();
      
      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract main content
      const newMainContent = doc.querySelector('#MainContent');
      
      if (newMainContent) {
        // Replace content
        this.mainContent.innerHTML = newMainContent.innerHTML;
        
        // Execute any scripts in the loaded content
        const scripts = this.mainContent.querySelectorAll('script');
        scripts.forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
        
        // Populate content from footer data attributes (fallback)
        setTimeout(() => {
          this.populateSectionContent(sectionParam);
          // Also try to call the global function if available
          if (window.loadFooterSectionContent) {
            window.loadFooterSectionContent();
          }
          // Set height to fill available space (with delay to ensure elements are rendered)
          if (window.setFooterSectionHeight) {
            setTimeout(() => {
              window.setFooterSectionHeight();
            }, 200);
            // Also try again after a longer delay
            setTimeout(() => {
              window.setFooterSectionHeight();
            }, 600);
          }
        }, 100);
        
        // Scroll to top
        window.scrollTo(0, 0);
        
        // Focus on title for accessibility
        setTimeout(() => {
          const title = this.mainContent.querySelector('.footer-section-page__title');
          if (title) {
            title.focus();
          }
        }, 200);
        
        // Add loaded class for transition
        setTimeout(() => {
          const footerSectionPage = this.mainContent.querySelector('.footer-section-page');
          if (footerSectionPage) {
            footerSectionPage.classList.add('is-loaded');
          }
        }, 10);
      }
      
    } catch (error) {
      console.error('Error loading footer section:', error);
      // Fallback: navigate normally
      window.location.href = `/pages/footer-section?section=${encodeURIComponent(sectionParam)}`;
    } finally {
      this.mainContent.classList.remove('is-loading');
    }
  }
  
  populateSectionContent(sectionParam) {
    // Find the footer trigger link that matches this section
    const footerTriggers = document.querySelectorAll('.footer__marble-trigger');
    let matchingTrigger = null;
    
    footerTriggers.forEach(trigger => {
      const heading = trigger.dataset.heading;
      if (heading && heading.toLowerCase() === sectionParam) {
        matchingTrigger = trigger;
      }
    });
    
    if (!matchingTrigger) return;
    
    const blockType = matchingTrigger.dataset.blockType;
    const heading = matchingTrigger.dataset.heading;
    const contentPlaceholder = this.mainContent.querySelector('.footer-section-page__content-placeholder');
    const list = this.mainContent.querySelector('#footer-section-list');
    const title = this.mainContent.querySelector('.footer-section-page__title');
    
    // Update title if we have the actual heading
    if (title && heading) {
      title.textContent = heading.toUpperCase();
    }
    
    // If content is already loaded (menu found via Liquid), skip
    if (contentPlaceholder && contentPlaceholder.dataset.loaded === 'true') {
      return;
    }
    
    // If no placeholder or list, content was loaded via Liquid, skip
    if (!contentPlaceholder || !list) {
      return;
    }
    
    if (blockType === 'link_list') {
      // Get menu handle and find menu links from footer
      const menuHandle = matchingTrigger.dataset.menuHandle;
      if (menuHandle) {
        // Find menu links in the footer
        const footerBlock = document.querySelector(`footer .footer-block[data-menu-handle="${menuHandle}"]`);
        if (footerBlock) {
          const footerMenuLinks = footerBlock.querySelectorAll('.footer-block__link');
          footerMenuLinks.forEach(link => {
            const li = document.createElement('li');
            li.className = 'footer-section-page__item';
            const a = document.createElement('a');
            a.href = link.href;
            a.className = 'footer-section-page__link';
            a.textContent = link.textContent.trim();
            li.appendChild(a);
            list.appendChild(li);
          });
          contentPlaceholder.dataset.loaded = 'true';
        }
      }
    } else if (blockType === 'contact') {
      // Handle contact block
      if (list) {
        list.className = 'footer-section-page__list footer-section-page__list--contact';
      }
      
      const phone = matchingTrigger.dataset.phone;
      const email = matchingTrigger.dataset.email;
      const address = matchingTrigger.dataset.address;
      
      if (phone) {
        const li = document.createElement('li');
        li.className = 'footer-section-page__item';
        const a = document.createElement('a');
        a.href = `tel:${phone.replace(/\s/g, '')}`;
        a.className = 'footer-section-page__link';
        a.textContent = `Tel/Cell: ${phone}`;
        li.appendChild(a);
        list.appendChild(li);
      }
      
      if (email) {
        const li = document.createElement('li');
        li.className = 'footer-section-page__item';
        const a = document.createElement('a');
        a.href = `mailto:${email}`;
        a.className = 'footer-section-page__link';
        a.textContent = email;
        li.appendChild(a);
        list.appendChild(li);
      }
      
      if (address) {
        const li = document.createElement('li');
        li.className = 'footer-section-page__item footer-section-page__item--address';
        const p = document.createElement('p');
        p.className = 'footer-section-page__text';
        p.textContent = address.replace(/\\n/g, '\n');
        li.appendChild(p);
        list.appendChild(li);
      }
      
      if (contentPlaceholder) {
        contentPlaceholder.dataset.loaded = 'true';
      }
    } else if (blockType === 'text') {
      // Handle text block - get content from footer
      const footerBlock = document.querySelector(`footer .footer-block[data-heading="${heading}"]`);
      if (footerBlock) {
        const textContent = footerBlock.querySelector('.footer-block__text');
        if (textContent) {
          const li = document.createElement('li');
          li.className = 'footer-section-page__item';
          const p = document.createElement('p');
          p.className = 'footer-section-page__text';
          p.innerHTML = textContent.innerHTML;
          li.appendChild(p);
          list.appendChild(li);
          if (contentPlaceholder) {
            contentPlaceholder.dataset.loaded = 'true';
          }
        }
      }
    }
  }
  
  handlePopState() {
    // Check if we're already on a footer section page
    const urlParams = new URLSearchParams(window.location.search);
    const sectionParam = urlParams.get('section');
    
    if (sectionParam && window.location.pathname.includes('footer-section')) {
      // We're on a footer section page, ensure proper state
      if (!history.state || !history.state.footerSection) {
        history.replaceState(
          { footerSection: sectionParam },
          '',
          window.location.href
        );
      }
      // Populate content on initial page load
      setTimeout(() => {
        this.populateSectionContent(sectionParam);
      }, 100);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FooterSectionNavigation();
  });
} else {
  new FooterSectionNavigation();
}

